import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  OnDestroy,
  signal,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SourceField } from '@sciflow/cite';
import { updateDocumentState } from 'editor';
import { selectDocumentStateIsDirty } from 'projects/editor/src/lib/editor.reducer';
import { ProjectService } from 'projects/reference-app/src/editor/shared/services/project.service';
import { SfoUiJSONSchema7 } from 'projects/ui-components/src';
import { firstValueFrom, Subject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { DcaStore, FileService } from 'shared';
import { ErrorDialogService } from '../error-dialog/error-dialog.service';
import { RenderingService } from '../rendering.service';
import { ReferenceManagementService } from '../services/reference-management.service';
import { setReferences } from '../store/references.actions';
import { selectCitationlist, selectReferenceList } from '../store/references.reducer';
import { Reference, ReferenceDraft } from '../reference.model';

// Import pre-resolved schemas from build output
import articleJournalSchema from '../../../../../../dist/libs/cite/src/csl-schemas/article-journal.schema.json';
import articleMagazineSchema from '../../../../../../dist/libs/cite/src/csl-schemas/article-magazine.schema.json';
import articleNewspaperSchema from '../../../../../../dist/libs/cite/src/csl-schemas/article-newspaper.schema.json';
import articleSchema from '../../../../../../dist/libs/cite/src/csl-schemas/article.schema.json';
import billSchema from '../../../../../../dist/libs/cite/src/csl-schemas/bill.schema.json';
import bookSchema from '../../../../../../dist/libs/cite/src/csl-schemas/book.schema.json';
import broadcastSchema from '../../../../../../dist/libs/cite/src/csl-schemas/broadcast.schema.json';
import chapterSchema from '../../../../../../dist/libs/cite/src/csl-schemas/chapter.schema.json';
import classicSchema from '../../../../../../dist/libs/cite/src/csl-schemas/classic.schema.json';
import collectionSchema from '../../../../../../dist/libs/cite/src/csl-schemas/collection.schema.json';
import datasetSchema from '../../../../../../dist/libs/cite/src/csl-schemas/dataset.schema.json';
import documentSchema from '../../../../../../dist/libs/cite/src/csl-schemas/document.schema.json';
import entryDictionarySchema from '../../../../../../dist/libs/cite/src/csl-schemas/entry-dictionary.schema.json';
import entryEncyclopediaSchema from '../../../../../../dist/libs/cite/src/csl-schemas/entry-encyclopedia.schema.json';
import entrySchema from '../../../../../../dist/libs/cite/src/csl-schemas/entry.schema.json';
import eventSchema from '../../../../../../dist/libs/cite/src/csl-schemas/event.schema.json';
import figureSchema from '../../../../../../dist/libs/cite/src/csl-schemas/figure.schema.json';
import graphicSchema from '../../../../../../dist/libs/cite/src/csl-schemas/graphic.schema.json';
import hearingSchema from '../../../../../../dist/libs/cite/src/csl-schemas/hearing.schema.json';
import interviewSchema from '../../../../../../dist/libs/cite/src/csl-schemas/interview.schema.json';
import legalCaseSchema from '../../../../../../dist/libs/cite/src/csl-schemas/legal-case.schema.json';
import legislationSchema from '../../../../../../dist/libs/cite/src/csl-schemas/legislation.schema.json';
import manuscriptSchema from '../../../../../../dist/libs/cite/src/csl-schemas/manuscript.schema.json';
import mapSchema from '../../../../../../dist/libs/cite/src/csl-schemas/map.schema.json';
import motionPictureSchema from '../../../../../../dist/libs/cite/src/csl-schemas/motion-picture.schema.json';
import musicalScoreSchema from '../../../../../../dist/libs/cite/src/csl-schemas/musical-score.schema.json';
import pamphletSchema from '../../../../../../dist/libs/cite/src/csl-schemas/pamphlet.schema.json';
import paperConferenceSchema from '../../../../../../dist/libs/cite/src/csl-schemas/paper-conference.schema.json';
import patentSchema from '../../../../../../dist/libs/cite/src/csl-schemas/patent.schema.json';
import performanceSchema from '../../../../../../dist/libs/cite/src/csl-schemas/performance.schema.json';
import periodicalSchema from '../../../../../../dist/libs/cite/src/csl-schemas/periodical.schema.json';
import personalCommunicationSchema from '../../../../../../dist/libs/cite/src/csl-schemas/personal-communication.schema.json';
import postSchema from '../../../../../../dist/libs/cite/src/csl-schemas/post.schema.json';
import postWeblogSchema from '../../../../../../dist/libs/cite/src/csl-schemas/post-weblog.schema.json';
import regulationSchema from '../../../../../../dist/libs/cite/src/csl-schemas/regulation.schema.json';
import reportSchema from '../../../../../../dist/libs/cite/src/csl-schemas/report.schema.json';
import reviewBookSchema from '../../../../../../dist/libs/cite/src/csl-schemas/review-book.schema.json';
import reviewSchema from '../../../../../../dist/libs/cite/src/csl-schemas/review.schema.json';
import softwareSchema from '../../../../../../dist/libs/cite/src/csl-schemas/software.schema.json';
import songSchema from '../../../../../../dist/libs/cite/src/csl-schemas/song.schema.json';
import speechSchema from '../../../../../../dist/libs/cite/src/csl-schemas/speech.schema.json';
import standardSchema from '../../../../../../dist/libs/cite/src/csl-schemas/standard.schema.json';
import thesisSchema from '../../../../../../dist/libs/cite/src/csl-schemas/thesis.schema.json';
import treatySchema from '../../../../../../dist/libs/cite/src/csl-schemas/treaty.schema.json';
import webpageSchema from '../../../../../../dist/libs/cite/src/csl-schemas/webpage.schema.json';

const preResolvedSchemas = [
  articleJournalSchema,
  articleMagazineSchema,
  articleNewspaperSchema,
  articleSchema,
  billSchema,
  bookSchema,
  broadcastSchema,
  chapterSchema,
  classicSchema,
  collectionSchema,
  datasetSchema,
  documentSchema,
  entryDictionarySchema,
  entryEncyclopediaSchema,
  entrySchema,
  eventSchema,
  figureSchema,
  graphicSchema,
  hearingSchema,
  interviewSchema,
  legalCaseSchema,
  legislationSchema,
  manuscriptSchema,
  mapSchema,
  motionPictureSchema,
  musicalScoreSchema,
  pamphletSchema,
  paperConferenceSchema,
  patentSchema,
  performanceSchema,
  periodicalSchema,
  personalCommunicationSchema,
  postSchema,
  postWeblogSchema,
  regulationSchema,
  reportSchema,
  reviewBookSchema,
  reviewSchema,
  softwareSchema,
  songSchema,
  speechSchema,
  standardSchema,
  thesisSchema,
  treatySchema,
  webpageSchema,
] as SfoUiJSONSchema7[];

@Component({
  selector: 'lib-references',
  templateUrl: './references.component.html',
  styleUrls: ['./references.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReferencesComponent implements OnDestroy {
  // Pre-resolved schemas from build output - no runtime dereferencing needed
  cslSchemas = signal<SfoUiJSONSchema7[]>(preResolvedSchemas);

  displayedColumns: string[] = ['bib', 'isStarred'];
  stop$ = new Subject<void>();
  loading = true;
  projectId: string;

  /** HTML element we use for as a drag image */
  dragElement;

  /** Selected reference for editing */
  activeReference = signal<any | null>(null);

  referenceList$ = this.store.select(selectReferenceList).pipe(
    switchMap((referenceList) =>
      this.store
        .select(selectCitationlist)
        .pipe(map((nodeCitations) => ({ nodeCitations, referenceList }))),
    ),
    map(({ nodeCitations, referenceList }) => {
      const citations: any[] = [];
      if (nodeCitations.length !== 0) {
        nodeCitations.forEach((element) => {
          element.citationItems.map((ref) =>
            citations.push({
              source: ref.id,
              id: element.citationID,
              textContent: element.plainCitation,
            }),
          );
        });
      }

      if (referenceList.length === 0) {
        this.loading = false;
        return [];
      }

      referenceList = referenceList.map((reference: any) => {
        const referencesInText = [] as any;
        reference = { ...reference };
        citations.map((citation) => {
          if (citation.source === reference.csl.id) {
            reference.citationsfromEditor = [];
            referencesInText.push(citation);
            reference.citationsfromEditor = referencesInText;
          }
        });
        return reference;
      });

      return referenceList as ReferenceDraft[];
    }),
  );

  isMaximized$ = this.dcaStore.isDcaExpanded;
  isReferenceEditingEnabled = signal(
    localStorage.getItem('references.editing.enabled') != undefined,
  );

  @ViewChild('fileUpload', { static: true }) fileUpload;

  constructor(
    private store: Store,
    private activatedRoute: ActivatedRoute,
    private renderingService: RenderingService,
    private fileService: FileService,
    private router: Router,
    private projectService: ProjectService,
    private errorDialogService: ErrorDialogService,
    private dcaStore: DcaStore,
    private referenceManagementService: ReferenceManagementService,
  ) {
    effect(() => {
      const isExpanded = this.dcaStore.isDcaExpanded();
      if (!isExpanded) {
        this.activeReference.set(null);
      }
    });
  }

  referenceSourceClass(reference: any): string | undefined {
    const source = (reference?.csl as any)?._meta?.source;
    if (source === 'ai') {
      return 'ai';
    }
    return undefined;
  }

  async uploadReferenceFile(): Promise<void> {
    this.fileUpload.nativeElement.click();
  }

  public async onUpload(event) {
    event.preventDefault();
    event.stopPropagation();
    const file = (event.srcElement || event.target).files.item(0);
    this.fileUpload.nativeElement.value = '';

    if (!file.name.endsWith('.bib')) {
      this.errorDialogService.openDialog('Only .bib files are currently supported.');
      return;
    }

    const result = await this.fileService.uploadFile(this.projectId, file);
    if (result) {
      const references = await this.projectService.getReferences(result.projectId);
      this.store.dispatch(setReferences({ references }));
    }
  }

  editCitationInText(citation): void {
    this.activeReference.set(null);
    this.dcaStore.collaspeDcaExpansion();

    const element = document.getElementById(citation.id);
    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
    element?.focus();
    this.router.navigate(['citation', citation.id], { relativeTo: this.activatedRoute });
  }

  editReference(reference): void {
    this.activeReference.set(reference);
    if (!this.dcaStore.isDcaExpanded()) {
      this.dcaStore.expandDcaWithMaximize();
    }
  }

  toggleMaximize(): void {
    this.dcaStore.toggleDcaExpansion();
  }

  ngOnDestroy(): void {
    this.stop$.next();
  }

  onDragStart(event, data): void {
    const isMaximized = this.dcaStore.isDcaMaximized();
    if (isMaximized) {
      event.preventDefault();
    } else {
      this.dragStart(event, data);
    }
  }

  async dragStart(event, data): Promise<void> {
    if (!data.csl?.id) {
      this.errorDialogService.openDialog('No citation data found');
      return;
    }

    const citation = this.renderingService.render([{ id: data.csl.id }], [data.csl]);
    const source = { citationItems: [{ id: data.csl.id as string }], properties: { noteIndex: 0 } };
    const cite = `<cite data-source="${encodeURI(SourceField.toString(source))}">${citation}</cite>`;

    this.dragElement = document.createElement('cite');
    this.dragElement.innerHTML = `<b>${citation}</b>`;
    document.body.appendChild(this.dragElement);
    event.dataTransfer.setDragImage(this.dragElement, 0, 25);

    event.dataTransfer.setData('text/html', cite);
  }

  dragEnd(event, row): void {
    if (this.dragElement) {
      document.body.removeChild(this.dragElement);
    }
  }

  selectReference(reference: any): void {
    this.activeReference.set(reference);
  }

  async updateReference(updatedReference: Reference): Promise<void> {
    try {
      const updatedBibEntries = this.renderingService.getBibliography([updatedReference.csl]);

      if (!updatedBibEntries || updatedBibEntries.length === 0) {
        return;
      }

      const csl: any = updatedReference.csl || {};
      if (csl?._meta?.source === 'ai') {
        csl._meta = { ...(csl._meta || {}), source: '' };
      }

      const newUpdatedReference: Reference = {
        ...updatedReference,
        csl,
        bib: updatedBibEntries[0],
      };

      const currentReferencesFromStore = await firstValueFrom(
        this.store.select(selectReferenceList).pipe(take(1)),
      );
      const newListOfAllReferences: Reference[] = currentReferencesFromStore.map((ref) =>
        ref.key === newUpdatedReference.key ? newUpdatedReference : ref,
      );

      this.store.dispatch(setReferences({ references: newListOfAllReferences }));

      console.log('[DEBUG] new list of references', newListOfAllReferences)

      const referenceId = newUpdatedReference.csl?.id;
      if (referenceId) {
        await this.referenceManagementService.updateAllCitationsForReference(referenceId);
      }

      const isDirty = await firstValueFrom(this.store.select(selectDocumentStateIsDirty));


      if (!isDirty) {
        this.store.dispatch(updateDocumentState({ dirty: true }));
      }
    } catch (error) {
      console.error('Error updating bibliography:', error);
      this.errorDialogService.openDialog('Failed to update bibliography formatting');
    }
  }
}
